'use client';

import { FC, Suspense, useEffect } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import { DayParam, GameType, dateToSimpleDate, dayArgToBoardDate } from '@puzzlio/core';
import { SiteFooter } from '@/app/components/new-ui/organisms/site-footer';

/**
 * Redirects to the timezone-correct "today" dated route (e.g. `/games/stars/2026-06-15`). Resolving
 * "today" on the client preserves the user's local date at week boundaries. Any legacy `?day=` (absolute
 * or numeric weekday) is resolved and forwarded too, and remaining query params (e.g. `nms`) are carried
 * over.
 */
export function ClientRedirectToToday({ gameType }: { gameType: GameType }) {
  const router = useRouter();
  const searchParams = useSearchParams();
  useEffect(() => {
    const today = dateToSimpleDate(new Date());

    const dayParam = searchParams.get('day') as DayParam | null;
    const target = (dayParam ? dayArgToBoardDate(dayParam, today) : null) ?? today;

    const rest = new URLSearchParams(searchParams.toString());
    rest.delete('day');
    const qs = rest.toString();
    router.replace(`/games/${gameType}/${target}${qs ? `?${qs}` : ''}`);
  }, [gameType, router, searchParams]);
  return null;
}

export const ClientRedirectToURL: FC<{ url: string; replace?: boolean }> = ({ url, replace = false }) => {
  const router = useRouter();
  useEffect(() => {
    if (replace) {
      router.replace(url);
    } else {
      router.push(url);
    }
  }, []);
  return null;
};

export const NoBoardFound: FC<{ dayParam: DayParam }> = ({ dayParam }) => {
  return (
    <Suspense fallback={<PageLoading />}>
      <div className="flex min-h-screen flex-col">
        <main className="flex grow items-center justify-center">
          <p>No board found for {dayParam}</p>
        </main>
        <SiteFooter />
      </div>
    </Suspense>
  );
};

export const PageLoading = () => {
  return <div className="flex min-h-screen items-center justify-center">Loading...</div>;
};
